/* global __SSR__ */

// NOTE: There is different Api file for the mobileApplication! If something is changed here, it should be changed there as well!

import { hideLoading, showLoading } from 'react-redux-loading-bar';

function formatUrl(path) {
    const adjustedPath = path[0] !== '/' ? `/${path}` : path;

    if (__SSR__) {
        // Prepend host and port of the server to the path.
        // return 'http://' + '127.0.0.1' + ':' + '3000' + adjustedPath;
        return `${process.env.SERVER_HOST}:${process.env.SERVER_PORT}${adjustedPath}`;
    }

    // Prepend `/api` to relative URL, to proxy to API server.
    return adjustedPath;
}

/**
 * Normalizes the responses from APIs so that the methods implemented here
 * can expect the same response format.
 *
 * @param response
 * @returns {Promise}
 */
function normalizeResponse(response) {
    // removed the status check so that we can handle the errors in the components
    // if (response.status >= 400) {
    //    return Promise.reject(response);
    // }
    return response.json();
}

/**
 * Implements utility methods that facilitate API calls.
 * Currently they are implemented as wrappers around the Fetch API. But the
 * clients are abstracted away from that.
 */
export default {
    /**
     * Makes an HTTP request using the given method and url. It accepts optional params and
     * authToken.
     * If present the authToken is sent in the Authorization header.
     *
     * @param {string} method The HTTP method to use (GET, POST, PUT, DELETE, etc)
     * @param {string} url The url
     * @param params Optional JSON object to be sent in the body
     * @param authToken Optional auth token to be sent in Authorization header.
     * @returns {Promise} Returns a promise that resolves to the response from the endpoint.
     */
    request(method, url, params, authToken) {
        const config = {
            method,
            headers: {
                Accept: 'application/json',
            },
            body: params ? JSON.stringify(params) : undefined,
            credentials: 'include',
        };

        if (['POST', 'DELETE', 'PUT'].indexOf(method.toUpperCase()) !== -1) {
            config.headers['Content-Type'] = 'application/json;charset=utf-8';
        }

        if (authToken) {
            config.headers.Authorization = `Bearer ${authToken}`;
        }

        return fetch(`${formatUrl(url)}`, config)
            .then(normalizeResponse)
            .catch((response) => {
                const { headers } = response;

                // Note: I think the following check is not more necessary as I now return json even in case of a not found error
                if (headers && headers.get('Content-Type').indexOf('application/json') === 0) {
                    return response.json().then((r) => Promise.reject(r));
                }

                return Promise.reject(response);
            });
    },

    /**
     * Shortcut method to perform GET request. See request()
     *
     * @param {string} url The url
     * @param authToken Optional auth token to be sent in Authorization header.
     * @returns {Promise} Returns a promise that resolves to the response from the endpoint.
     */
    get(url, authToken) {
        return this.request('GET', url, null, authToken);
    },

    /**
     * Shortcut method to perform DELETE request. See request()
     *
     * @param {string} url The url
     * @param authToken Optional auth token to be sent in Authorization header.
     * @returns {Promise} Returns a promise that resolves to the response from the endpoint.
     */
    del(url, authToken) {
        return this.request('DELETE', url, null, authToken);
    },

    /**
     * Shortcut method to perform POST request. See request()
     *
     * @param {string} url The url
     * @param params Optional JSON object to be sent in the body
     * @param authToken Optional auth token to be sent in Authorization header.
     * @returns {Promise} Returns a promise that resolves to the response from the endpoint.
     */
    post(url, params, authToken) {
        return this.request('POST', url, params, authToken);
    },

    /**
     * Shortcut method to perform PUT request. See request()
     *
     * @param {string} url The url
     * @param params Optional JSON object to be sent in the body
     * @param authToken Optional auth token to be sent in Authorization header.
     * @returns {Promise} Returns a promise that resolves to the response from the endpoint.
     */
    put(url, params, authToken) {
        return this.request('PUT', url, params, authToken);
    },

    async progressGet(dispatch, url, authToken) {
        try {
            dispatch(showLoading());

            return await this.get(url, authToken);
        } finally {
            dispatch(hideLoading());
        }
    },

    async progressPost(dispatch, url, params, authToken) {
        try {
            dispatch(showLoading());

            return await this.post(url, params, authToken);
        } finally {
            dispatch(hideLoading());
        }
    },

    async progressPut(dispatch, url, params, authToken) {
        try {
            dispatch(showLoading());

            return await this.put(url, params, authToken);
        } finally {
            dispatch(hideLoading());
        }
    },
};
